import React, { useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { IconSpinner, Alert } from '../../components';
import Switch from './Switch';
import InlineConfirm from './InlineConfirm';
import TriggerActivityPanel from './TriggerActivityPanel';
import { TRIGGER_LABELS } from './WorkflowEditor/triggerLabels';
import { PLATFORM_LABELS } from './platformLabels';
import {
    unpublishedDraftNotice,
    unpublishedDraftSummary,
    workflowDeleteBlockedNotice,
} from './dashboardHelpers';
import {
    fetchWorkflows,
    fetchTriggers,
    setWorkflowEnabled,
    renameWorkflow,
    createWorkflow,
    duplicateWorkflow,
    deleteWorkflow,
} from './AdminSocialPostsPage.duck';
import css from './AdminSocialPostsPage.css';

// The German label for a workflow's derived trigger: the server registry wins,
// then the client fallback map, then the raw key. A null trigger = "kein Auslöser"
// (the workflow never auto-fires).
const triggerLabel = (trigger, triggers) => {
    if (!trigger) return 'Kein Auslöser';
    const found = (triggers || []).find(t => t.key === trigger);
    return (found && found.label) || TRIGGER_LABELS[trigger] || trigger;
};

const normalizeName = name => {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed || 'Neuer Workflow';
};

// Active platforms are derived from enabled platform_exit nodes of the PUBLISHED
// graph. Disabled nodes behave like deleted nodes, so the list mirrors the
// executable workflow.
const activePlatforms = workflow => {
    const nodes = (workflow && workflow.graph && workflow.graph.nodes) || [];
    const seen = [];
    nodes.forEach(n => {
        if (!n || n.enabled === false || n.type !== 'platform_exit') return;
        const p = n && n.params && n.params.platform;
        if (typeof p === 'string' && p && !seen.includes(p)) seen.push(p);
    });
    return seen;
};

// The other ENABLED workflow sharing this one's trigger (for the "läuft parallel
// zu" info chip) — only when this workflow itself is enabled and its trigger is
// set. Returns one sibling's name, or null.
const parallelSiblingName = (workflow, workflows) => {
    if (!workflow.enabled || !workflow.trigger) return null;
    const sibling = (workflows || []).find(
        w => w.slug !== workflow.slug && w.enabled && w.trigger === workflow.trigger
    );
    return sibling ? sibling.name || 'Standard' : null;
};

/**
 * The enable/disable Switch — the shared on-brand Switch, NEVER colour-alone: a
 * text label ("Aktiv" / "Inaktiv") sits beside the forward-green track and
 * aria-checked + aria-busy expose the state and the in-flight toggle to
 * assistive tech. Optimistic: the parent flips the row instantly and rolls back
 * on error. `stopPropagation` keeps the click off the row's open handler.
 */
const EnableSwitch = ({ enabled, busy, onToggle, label }) => (
    <Switch
        checked={enabled}
        onChange={onToggle}
        onLabel="Aktiv"
        offLabel="Inaktiv"
        tone="success"
        busy={busy}
        ariaLabel={label}
        stopPropagation
    />
);

const WorkflowRow = ({
    workflow,
    workflows,
    triggers,
    busy,
    renaming,
    deleting,
    duplicating,
    deleteBlocked,
    onOpen,
    onOpenPosts,
    onToggle,
    onRename,
    onDuplicate,
    onDelete,
}) => {
    const platforms = activePlatforms(workflow);
    const platformText = platforms.map(p => PLATFORM_LABELS[p] || p).join(' · ');
    const label = triggerLabel(workflow.trigger, triggers);
    const currentName = workflow.name || 'Standard';
    // An unpublished draft is the row's most consequential fact: the studio edits
    // are saved but NOT live. A chip alone was too quiet to carry that.
    const draftNotice = unpublishedDraftNotice(workflow);
    const parallelName = parallelSiblingName(workflow, workflows);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(currentName);
    const [showActivity, setShowActivity] = useState(false);

    const toggleActivity = e => {
        e.stopPropagation();
        setShowActivity(v => !v);
    };

    useEffect(() => {
        if (!editing) setDraft(currentName);
    }, [currentName, editing]);

    const startEdit = e => {
        e.stopPropagation();
        setDraft(currentName);
        setEditing(true);
    };

    const cancelEdit = e => {
        if (e) e.stopPropagation();
        setDraft(currentName);
        setEditing(false);
    };

    const saveEdit = e => {
        if (e) e.stopPropagation();
        const nextName = normalizeName(draft);
        if (renaming) return;
        if (nextName === currentName) {
            setEditing(false);
            return;
        }
        onRename(workflow.slug, nextName)
            .then(() => setEditing(false))
            .catch(() => {});
    };

    return (
        <div className={css.wfRowWrap}>
            <div
                role="button"
                tabIndex={0}
                className={`${css.wfRow} ${workflow.enabled ? '' : css.wfRowOff}`}
                onClick={() => onOpen(workflow)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpen(workflow);
                    }
                }}
            >
                <div className={css.wfRowBody}>
                    <div className={css.wfTitleLine}>
                        {editing ? (
                            <input
                                className={`${css.wfRowTitle} ${css.wfRenameInput}`}
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                onKeyDown={e => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') saveEdit(e);
                                    if (e.key === 'Escape') cancelEdit(e);
                                }}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                                onBlur={saveEdit}
                                autoFocus
                                aria-label={`Workflow „${currentName}“ umbenennen`}
                            />
                        ) : (
                            <p className={css.wfRowTitle}>{currentName}</p>
                        )}
                        {/* An unpublished draft exists (studio edits not yet live). */}
                        {draftNotice ? (
                            <span className={css.wfDraftChip}>{draftNotice.label}</span>
                        ) : null}
                        {editing ? (
                            <button
                                type="button"
                                className={css.wfRenameLink}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onClick={cancelEdit}
                                onKeyDown={e => e.stopPropagation()}
                            >
                                Abbrechen
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={css.wfRenameLink}
                                onClick={startEdit}
                                onKeyDown={e => e.stopPropagation()}
                                aria-label={`Workflow „${currentName}“ umbenennen`}
                            >
                                Umbenennen
                            </button>
                        )}
                    </div>
                    <p className={css.wfRowMeta}>
                        <span
                            className={`${css.wfRowTrigger} ${
                                workflow.trigger ? '' : css.wfRowTriggerMuted
                            }`}
                        >
                            {label}
                        </span>
                        <span className={css.cardDot}>·</span>
                        <span>{platformText || 'Keine Plattform'}</span>
                        <span className={css.cardDot}>·</span>
                        <button
                            type="button"
                            className={css.wfActivityToggle}
                            onClick={toggleActivity}
                            onKeyDown={e => e.stopPropagation()}
                            aria-expanded={showActivity}
                        >
                            Aktivität {showActivity ? '▲' : '▼'}
                        </button>
                    </p>
                    {parallelName ? (
                        <p className={css.wfParallelChip}>läuft parallel zu „{parallelName}“</p>
                    ) : null}
                    {/* The unmissable half of the draft state: one plain sentence
                        saying what is (and is not) live, plus the way to finish it.
                        Publishing happens in the studio on purpose — a rejected
                        publish points at the offending node on the canvas, which a
                        list row cannot show. */}
                    {draftNotice ? (
                        <p className={css.wfParallelChip}>
                            {draftNotice.text}{' '}
                            <button
                                type="button"
                                className={css.wfRenameLink}
                                onClick={e => {
                                    e.stopPropagation();
                                    onOpen(workflow);
                                }}
                                onKeyDown={e => e.stopPropagation()}
                            >
                                {draftNotice.actionLabel} →
                            </button>
                        </p>
                    ) : null}
                    {/* A blocked delete (409, posts still in flight) used to end
                        here as a red string with nowhere to go. */}
                    {deleteBlocked ? (
                        <p className={css.wfParallelChip}>
                            {deleteBlocked.text}
                            {deleteBlocked.action ? (
                                <>
                                    {' '}
                                    <button
                                        type="button"
                                        className={css.wfRenameLink}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onOpenPosts(deleteBlocked.action.href);
                                        }}
                                        onKeyDown={e => e.stopPropagation()}
                                    >
                                        {deleteBlocked.action.label} →
                                    </button>
                                </>
                            ) : null}
                        </p>
                    ) : null}
                    {/* Row actions: duplicate + delete (2-step inline confirm). */}
                    <div className={css.wfRowActions}>
                        <button
                            type="button"
                            className={css.wfRowAction}
                            disabled={duplicating}
                            onClick={e => {
                                e.stopPropagation();
                                onDuplicate(workflow.slug);
                            }}
                            onKeyDown={e => e.stopPropagation()}
                        >
                            {duplicating ? <IconSpinner /> : 'Duplizieren'}
                        </button>
                        <InlineConfirm
                            triggerLabel="Löschen"
                            triggerClassName={css.wfRowActionDanger}
                            question={`„${currentName}“ löschen?`}
                            note="Bereits erzeugte Beiträge bleiben bestehen."
                            confirmLabel="Löschen"
                            danger
                            busy={deleting}
                            onConfirm={() => onDelete(workflow.slug)}
                        />
                    </div>
                </div>
                <span
                    className={`${css.statusPill} ${
                        workflow.enabled ? css.statusPublished : css.statusNeutral
                    }`}
                >
                    {workflow.enabled ? 'Läuft' : 'Pausiert'}
                </span>
                <EnableSwitch
                    enabled={workflow.enabled}
                    busy={busy}
                    label={`Workflow „${currentName}“ ${
                        workflow.enabled ? 'deaktivieren' : 'aktivieren'
                    }`}
                    onToggle={() => onToggle(workflow.slug, !workflow.enabled)}
                />
            </div>
            {showActivity && <TriggerActivityPanel slug={workflow.slug} />}
        </div>
    );
};

const WorkflowList = props => {
    const {
        history,
        workflows,
        triggers,
        fetchWorkflowsInProgress,
        fetchWorkflowsError,
        workflowsTogglingSlugs,
        workflowsRenamingSlugs,
        deletingWorkflowSlugs,
        duplicateWorkflowInProgress,
        workflowsError,
        renameWorkflowError,
        createWorkflowInProgress,
        createWorkflowError,
        duplicateWorkflowError,
        deleteWorkflowError,
        onFetchWorkflows,
        onFetchTriggers,
        onSetEnabled,
        onRenameWorkflow,
        onCreateWorkflow,
        onDuplicateWorkflow,
        onDeleteWorkflow,
    } = props;

    const [creating, setCreating] = useState(false);
    // WHICH row's delete failed. The duck carries one global deleteWorkflowError
    // string; the operator needs it attached to the row they clicked, with the
    // count and the next step spelled out.
    const [deleteAttemptSlug, setDeleteAttemptSlug] = useState(null);

    useEffect(() => {
        onFetchWorkflows();
        onFetchTriggers();
    }, [onFetchWorkflows, onFetchTriggers]);

    const openWorkflow = useCallback(
        workflow => {
            if (!workflow || !workflow.slug) return;
            history.push(`/admin-social/workflow/${encodeURIComponent(workflow.slug)}`);
        },
        [history]
    );

    // The way out of a blocked delete: the post list, filtered to this workflow.
    const openPosts = useCallback(href => history.push(href), [history]);

    const handleDelete = useCallback(
        slug => {
            setDeleteAttemptSlug(slug);
            return onDeleteWorkflow(slug);
        },
        [onDeleteWorkflow]
    );

    const toggle = useCallback(
        (slug, enabled) => {
            onSetEnabled(slug, enabled).catch(() => {});
        },
        [onSetEnabled]
    );

    // "+ Neuer Workflow": create a blank, paused workflow, then jump straight into
    // its studio to pick an Auslöser and wire it up.
    const handleCreate = useCallback(async () => {
        setCreating(true);
        try {
            const workflow = await onCreateWorkflow();
            openWorkflow(workflow);
        } catch (e) {
            // error surfaced via createWorkflowError
        } finally {
            setCreating(false);
        }
    }, [onCreateWorkflow, openWorkflow]);

    // "Duplizieren": copy the published graph into a new paused workflow, then open it.
    const handleDuplicate = useCallback(
        async slug => {
            try {
                const workflow = await onDuplicateWorkflow(slug);
                openWorkflow(workflow);
            } catch (e) {
                // error surfaced via duplicateWorkflowError
            }
        },
        [onDuplicateWorkflow, openWorkflow]
    );

    const draftSummary = unpublishedDraftSummary(workflows);
    const deleteBlocked = deleteAttemptSlug
        ? workflowDeleteBlockedNotice(deleteWorkflowError, deleteAttemptSlug)
        : null;

    if (fetchWorkflowsInProgress && workflows.length === 0) {
        return (
            <div className={css.centered}>
                <IconSpinner />
            </div>
        );
    }

    return (
        <div className={css.wfListWrap}>
            {fetchWorkflowsError && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={fetchWorkflowsError}
                    closeAllowed={false}
                />
            )}
            {workflowsError && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={`Umschalten fehlgeschlagen: ${workflowsError}`}
                    closeAllowed={false}
                />
            )}
            {renameWorkflowError && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={`Umbenennen fehlgeschlagen: ${renameWorkflowError}`}
                    closeAllowed={false}
                />
            )}
            {createWorkflowError && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={`Workflow konnte nicht erstellt werden: ${createWorkflowError}`}
                    closeAllowed={false}
                />
            )}
            {duplicateWorkflowError && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={`Duplizieren fehlgeschlagen: ${duplicateWorkflowError}`}
                    closeAllowed={false}
                />
            )}
            {/* A delete that failed for a reason we can act on is shown ON the row
                (with the count + the next step); anything else stays here. */}
            {deleteWorkflowError && !deleteBlocked && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={`Löschen fehlgeschlagen: ${deleteWorkflowError}`}
                    closeAllowed={false}
                />
            )}

            {/* Unpublished drafts, counted and named, above the list — the thing an
                operator must not be able to scroll past. Calm on purpose: nothing
                is broken, a step is simply unfinished. */}
            {draftSummary && (
                <Alert
                    showAsPlainText
                    type="info"
                    description={`${draftSummary.message} ${draftSummary.hint}`}
                    closeAllowed={false}
                />
            )}

            <div className={css.wfCreateRow}>
                <button
                    type="button"
                    className={css.wfCreateBtn}
                    disabled={creating || createWorkflowInProgress}
                    onClick={handleCreate}
                >
                    {creating || createWorkflowInProgress ? <IconSpinner /> : '+ Neuer Workflow'}
                </button>
            </div>

            {workflows.length === 0 ? (
                <Alert
                    showAsPlainText
                    type="info"
                    description="Noch keine Workflows. Erstelle einen mit „+ Neuer Workflow“."
                    closeAllowed={false}
                />
            ) : (
                <div className={css.wfList}>
                    {workflows.map(w => (
                        <WorkflowRow
                            key={w.slug}
                            workflow={w}
                            workflows={workflows}
                            triggers={triggers}
                            busy={workflowsTogglingSlugs.includes(w.slug)}
                            renaming={workflowsRenamingSlugs.includes(w.slug)}
                            deleting={deletingWorkflowSlugs.includes(w.slug)}
                            duplicating={duplicateWorkflowInProgress}
                            deleteBlocked={
                                deleteBlocked && deleteAttemptSlug === w.slug ? deleteBlocked : null
                            }
                            onOpen={openWorkflow}
                            onOpenPosts={openPosts}
                            onToggle={toggle}
                            onRename={onRenameWorkflow}
                            onDuplicate={handleDuplicate}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = state => {
    const s = state.AdminSocialPostsPage;
    return {
        workflows: s.workflows,
        triggers: s.triggers,
        fetchWorkflowsInProgress: s.fetchWorkflowsInProgress,
        fetchWorkflowsError: s.fetchWorkflowsError,
        workflowsTogglingSlugs: s.workflowsTogglingSlugs,
        workflowsRenamingSlugs: s.workflowsRenamingSlugs,
        deletingWorkflowSlugs: s.deletingWorkflowSlugs,
        duplicateWorkflowInProgress: s.duplicateWorkflowInProgress,
        workflowsError: s.workflowsError,
        renameWorkflowError: s.renameWorkflowError,
        createWorkflowInProgress: s.createWorkflowInProgress,
        createWorkflowError: s.createWorkflowError,
        duplicateWorkflowError: s.duplicateWorkflowError,
        deleteWorkflowError: s.deleteWorkflowError,
    };
};

const mapDispatchToProps = dispatch => ({
    onFetchWorkflows: () => dispatch(fetchWorkflows()),
    onFetchTriggers: () => dispatch(fetchTriggers()),
    onSetEnabled: (slug, enabled) => dispatch(setWorkflowEnabled(slug, enabled)),
    onRenameWorkflow: (slug, name) => dispatch(renameWorkflow(slug, name)),
    onCreateWorkflow: name => dispatch(createWorkflow(name)),
    onDuplicateWorkflow: slug => dispatch(duplicateWorkflow(slug)),
    onDeleteWorkflow: slug => dispatch(deleteWorkflow(slug)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowList);
